import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormInstance } from 'antd';
import authService from '../services/authService';
import { useDebounce } from './useDebounce';

/** Backend rejects a lookup key shorter than this (companyController.getUserCompany). */
const MIN_LOOKUP_LENGTH = 3;

/**
 * Company dropdown behaviour shared by the auth screens.
 *
 * - Loads every company on mount.
 * - When an E-Code/email is typed, resolves the companies that user belongs to:
 *   exactly one   -> auto-select it and lock the dropdown (nothing to choose),
 *   more than one -> narrow the dropdown to those companies, user picks one,
 *   no match      -> fall back to the full list, dropdown stays open.
 *
 * The hook writes `company_id` into the passed form instance.
 */
export const useCompanyOptions = (form: FormInstance) => {
  const [companies, setCompanies] = useState<any[]>([]);
  const [matchedCompanies, setMatchedCompanies] = useState<any[] | null>(null);
  const [companyLocked, setCompanyLocked] = useState(false);
  // The last key the backend confirmed matches no active user. Kept as the key
  // itself (not a flag) so a caller can tell whether the verdict belongs to
  // what is in the field right now or to something typed before it.
  const [unknownKey, setUnknownKey] = useState<string | null>(null);
  const [resolvingCompany, setResolvingCompany] = useState(false);
  const [inputCode, setInputCode] = useState('');
  const debouncedCode = useDebounce(inputCode.trim(), 500);

  // Only the newest lookup is allowed to write state — a slow earlier response
  // must not overwrite the result of what the user has typed since.
  const lookupSeq = useRef(0);

  useEffect(() => {
    let active = true;
    authService.getCompanies().then((list: any) => {
      if (active) setCompanies(list || []);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const requestId = ++lookupSeq.current;

    if (debouncedCode.length < MIN_LOOKUP_LENGTH) {
      // Too short to resolve — offer the full list again.
      setMatchedCompanies(null);
      setCompanyLocked(false);
      setUnknownKey(null);
      setResolvingCompany(false);
      return;
    }

    setResolvingCompany(true);
    authService
      .getUserCompany(debouncedCode)
      .then((result) => {
        if (requestId !== lookupSeq.current) return; // stale response
        const matches = result?.companies || [];

        // `result === null` means the lookup failed — the key is not proven
        // absent, so no "not on record" verdict is recorded for it.
        setUnknownKey(result && matches.length === 0 ? debouncedCode : null);

        if (matches.length === 1) {
          // Single account — auto-select and lock the dropdown.
          setMatchedCompanies(matches);
          setCompanyLocked(true);
          form.setFieldsValue({ company_id: result?.company_id ?? matches[0].id });
          return;
        }

        setCompanyLocked(false);

        if (matches.length > 1) {
          // Several accounts — offer only those companies, user must choose.
          setMatchedCompanies(matches);
          const current = form.getFieldValue('company_id');
          const stillValid = matches.some((c: any) => c.id === current);
          form.setFieldsValue({ company_id: stillValid ? current : undefined });
        } else {
          // No match — fall back to every company.
          setMatchedCompanies(null);
          form.setFieldsValue({ company_id: undefined });
        }
      })
      .catch(() => {
        if (requestId !== lookupSeq.current) return;
        setMatchedCompanies(null);
        setCompanyLocked(false);
        setUnknownKey(null);
      })
      .finally(() => {
        if (requestId === lookupSeq.current) setResolvingCompany(false);
      });
  }, [debouncedCode, form]);

  /**
   * Drop the resolved match immediately on keystroke so a locked dropdown does
   * not keep showing the previous user's company while the debounce settles.
   */
  const handleCodeChange = useCallback((value: string) => {
    setInputCode(value);
    setCompanyLocked(false);
  }, []);

  return {
    companyData: matchedCompanies ?? companies,
    companyLocked,
    resolvingCompany,
    unknownKey,
    setInputCode: handleCodeChange,
  };
};
